import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { translations } from 'src/constants/translations';
import { Breadcrumbs, ICrumbs } from 'src/components/profileComponents/Breadcrumbs';
import { pathList } from 'src/containers/Profile/routeList';
import { useParams } from 'react-router-dom';
import { useMarketplaces } from 'src/hooks/marketplace';
import {
  ActionButtons,
} from 'src/components/profileComponents/Marketplace/components/Filters/components/ActionButtons';
import {
  LocationGroup,
} from 'src/components/profileComponents/Marketplace/components/Filters/components/LocationGroup';
import {
  LocationButtons,
} from 'src/components/profileComponents/Marketplace/components/Filters/components/LocationGroup/components/LocationButtons';
import { SubGroupWrapper } from 'src/components/profileComponents/Marketplace/components/SubGroupWrapper';
import {
  LocationSubGroupElements,
} from 'src/components/profileComponents/Marketplace/components/Filters/components/LocationSubGroupElements';
import {
  LocationSubGroupLocationGroup,
} from 'src/components/profileComponents/Marketplace/components/Filters/components/LocationSubGroupLocationGroup';
import { TagAllValues } from 'src/components/profileComponents/Marketplace/components/Filters/components/Tag';
import { Range } from 'src/components/profileComponents/Marketplace/components/Filters/components/Range';
import { TagFilter } from 'src/components/profileComponents/Marketplace/components/Filters/components/TagFilter';
import {
  PrimaryTagFilter,
} from 'src/components/profileComponents/Marketplace/components/Filters/components/PrimaryTagFilter';
import {
  LocationButtonsEnum,
  primarySelectionFilters,
  primarySelectionTags,
  secondaryFilters,
} from 'src/components/profileComponents/Marketplace/components/Filters/constants';
import {
  IMarketplace,
  IMarketplaceFilterLocation,
  IMarketplaceFilterLocationFilter,
  IMarketplaceFilterRange,
  IMarketplaceFilterTagAllValues, IMarketplaceFilterTagSelectedValueSelectedProperty,
  IMarketplaceFilterTagSelectedValues,
  IMarketplaceFilterType,
  IMarketplaceModuleFiltersConfiguration, IMarketplaceModulePrimarySectionType,
  IMarketplaceProperty,
} from 'src/components/profileComponents/Marketplace/types';
import { RandomInteger } from 'src/helpers/randomInteger';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ButtonApp } from 'src/components/Button';
import {
  IDevelopmentSpecificationPropChange,
} from 'src/components/profileComponents/Complexes/components/ProjectSpecification/types';
import { ReactSortable, Sortable, Store } from 'react-sortablejs';
import { postMarketplace, putMarketplace } from 'src/api/marketplace';
import { useGetAuthData } from 'src/hooks';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { Checkbox } from 'src/components/profileComponents/Marketplace/components/Filters/components/Checkbox';
import { IMarketplaceLanguageObjects } from 'src/typings/marketplace';
import {
  AdditionalSettings,
} from 'src/components/profileComponents/Marketplace/components/Filters/components/AdditionalSettings';
import { useToastr } from 'src/hooks/useToastr';
import {
  isNeedToBeUpdated,
} from 'src/components/profileComponents/Marketplace/components/Common/helpers/PropertyValueChecker';
import { useStyles } from './styles';

export const Filters: React.FC = () => {
  const { id }: { id: string } = useParams();
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();
  const {
    getMarketplace, marketplaceProperties, fetchMarketplaces, requestMarketplaceLanguageObjects,
  } = useMarketplaces();
  const marketplace = getMarketplace(id);
  const [primarySectionFilters, setPrimarySectionFilters] = useState<IMarketplaceModuleFiltersConfiguration | undefined>();
  const [marketplacePropertyOptions, setMarketplacePropertyOptions] = useState<IDevelopmentSpecificationPropChange<IMarketplaceProperty>[]>([]);
  const [allSelectedProperties, setAllSelectedProperties] = useState<IMarketplaceLanguageObjects>();
  const [locationMarketplacePropertyOptions, setLocationMarketplacePropertyOptions] = useState<IDevelopmentSpecificationPropChange<IMarketplaceProperty>[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { toastrClear, toastrSuccess } = useToastr();
  const styles = useStyles();

  const {
    handleSubmit,
  } = useForm({
    resolver: yupResolver(yup.object().shape({})),
    mode: 'onChange',
  });

  useEffect(() => {
    if (marketplace) {
      setPrimarySectionFilters(marketplace?.frontConfiguration.module.filtersConfiguration);
      const allSystemNames = marketplace?.frontConfiguration.module.filtersConfiguration.primarySection.tag.selectedProperties
        .map((item) => item.property?.systemName || '')
        .filter((systemName) => systemName.length);
      requestMarketplaceLanguageObjects({
        marketplaceId: marketplace.id,
        findProperties: allSystemNames,
      }).then((res) => {
        setAllSelectedProperties(res.data);
        setIsLoading(false);
      });
    }
  }, [marketplace]);

  useEffect(() => {
    if (marketplaceProperties && marketplace) {
      const locationsProperties = marketplace.placementProperties.filter((item) => item.systemName.startsWith('complex.location'));
      const propertyOptions = locationsProperties.map((item) => ({ label: item.translates?.[marketplace.language] || item.systemName, value: item }));
      setMarketplacePropertyOptions(marketplace.placementProperties.map((item) => ({ label: item.translates?.[marketplace.language] || item.systemName, value: item })));
      setLocationMarketplacePropertyOptions(propertyOptions);
    }
  }, [marketplaceProperties, marketplace]);

  const updatePrimarySelection = (updatedItem: IMarketplaceModulePrimarySectionType) => {
    setPrimarySectionFilters((current) => {
      if (!current) return current;
      return {
        ...current,
        primarySection: {
          ...current.primarySection,
          selections: current.primarySection.selections.map((item) => (
            item.id === updatedItem.id ? updatedItem : item
          )),
        },
      };
    });
  };

  const updateSecondarySelection = (updatedItem: IMarketplaceModulePrimarySectionType) => {
    setPrimarySectionFilters((current) => {
      if (!current) return current;
      return {
        ...current,
        secondaries: current.secondaries.map((item) => (
          item.id === updatedItem.id ? updatedItem : item
        )),
      };
    });
  };

  const updatePrimaryLocationFilter = (parentId: string, updatedFilter: IMarketplaceFilterLocationFilter) => {
    setPrimarySectionFilters((current) => {
      if (!current) return current;
      return {
        ...current,
        primarySection: {
          ...current.primarySection,
          selections: current.primarySection.selections.map((item) => (
            item.id === parentId
              ? {
                ...item,
                filters: (item as IMarketplaceFilterLocation).filters.map((filter) => (
                  filter.id === updatedFilter.id ? updatedFilter : filter
                )),
              } as IMarketplaceFilterLocation
              : item
          )),
        },
      };
    });
  };

  const updateSecondaryLocationFilter = (parentId: string, updatedFilter: IMarketplaceFilterLocationFilter) => {
    setPrimarySectionFilters((current) => {
      if (!current) return current;
      return {
        ...current,
        secondaries: current.secondaries.map((item) => (
          item.id === parentId
            ? {
              ...item,
              filters: (item as IMarketplaceFilterLocation).filters.map((filter) => (
                filter.id === updatedFilter.id ? updatedFilter : filter
              )),
            } as IMarketplaceFilterLocation
            : item
        )),
      };
    });
  };

  const updatePrimaryTag = (tag: IMarketplaceFilterTagSelectedValues) => {
    setPrimarySectionFilters((current) => {
      if (!current) return current;
      return {
        ...current,
        primarySection: { ...current.primarySection, tag },
      };
    });
  };

  const updatePrimaryTagProperty = (updatedProperty: IMarketplaceFilterTagSelectedValueSelectedProperty) => {
    setPrimarySectionFilters((current) => {
      if (!current) return current;
      return {
        ...current,
        primarySection: {
          ...current.primarySection,
          tag: {
            ...current.primarySection.tag,
            selectedProperties: current.primarySection.tag.selectedProperties.map((property) => (
              property.id === updatedProperty.id ? updatedProperty : property
            )),
          },
        },
      };
    });
  };

  const updateSecondaryTagProperty = (
    parentId: string,
    updatedProperty: IMarketplaceFilterTagSelectedValueSelectedProperty,
  ) => {
    setPrimarySectionFilters((current) => {
      if (!current) return current;
      return {
        ...current,
        secondaries: current.secondaries.map((item) => (
          item.id === parentId
            ? {
              ...item,
              selectedProperties: (item as IMarketplaceFilterTagSelectedValues).selectedProperties.map((property) => (
                property.id === updatedProperty.id ? updatedProperty : property
              )),
            } as IMarketplaceFilterTagSelectedValues
            : item
        )),
      };
    });
  };

  const primarySelectionFiltersOnClick = (type: IMarketplaceFilterType) => {
    if (marketplace) {
      setPrimarySectionFilters((current) => (current ? ({
        ...current,
        primarySection: {
          ...current.primarySection,
          selections: [...current.primarySection.selections, {
            id: RandomInteger().toString(),
            type,
            sortOrder: current.primarySection.selections.length + 1,
            property: marketplacePropertyOptions?.[0]?.value || null,
            name: { [marketplace?.language]: '' },
            callToActionText: { [marketplace?.language]: '' },
            accordionState: 'none',
            isAccordionOpen: true,
            filters: [],
            tooltip: null,
          }],
        },
      }) : current));
    }
  };

  const secondarySelectionFiltersOnClick = (type: IMarketplaceFilterType) => {
    if (marketplace) {
      setPrimarySectionFilters((current) => {
        if (!current) return current;
        const value: IMarketplaceModulePrimarySectionType = {
          id: RandomInteger().toString(),
          type,
          sortOrder: current.secondaries.length + 1,
          name: { [marketplace?.language]: '' },
          callToActionText: { [marketplace?.language]: '' },
          accordionState: 'none',
          isAccordionOpen: true,
          filters: [],
          tooltip: null,
        };
        if (type === IMarketplaceFilterType.SelectedValuesTag) {
          (value as unknown as IMarketplaceFilterTagSelectedValues).selectedProperties = [];
        }
        return { ...current, secondaries: [...current.secondaries, value] };
      });
    }
  };

  const primarySelectionTagsOnClick = () => {
    if (marketplace) {
      setPrimarySectionFilters((current) => (current ? ({
        ...current,
        primarySection: {
          ...current.primarySection,
          tag: {
            ...current.primarySection.tag,
            selectedProperties: [...current.primarySection.tag.selectedProperties, {
              id: RandomInteger().toString(),
              sortOrder: current.primarySection.tag.selectedProperties.length + 1,
              property: {
                systemName: '',
                oldSystemName: '',
                translates: { [marketplace?.language]: '' },
              },
              value: { [marketplace?.language]: '' },
              iconFileUri: null,
            }],
          },
        },
      }) : current));
    }
  };

  const primarySelectedTagOnClick = (index: number) => {
    if (marketplace) {
      setPrimarySectionFilters((current) => {
        if (!current) return current;
        return {
          ...current,
          secondaries: current.secondaries.map((item, itemIndex) => {
            if (itemIndex !== index) return item;
            const tag = item as IMarketplaceFilterTagSelectedValues;
            return {
              ...tag,
              selectedProperties: [...tag.selectedProperties, {
                id: RandomInteger().toString(),
                sortOrder: tag.selectedProperties.length + 1,
                property: {
                  systemName: '',
                  oldSystemName: '',
                  translates: { [marketplace?.language]: '' },
                },
                value: { [marketplace?.language]: '' },
                iconFileUri: null,
              }],
            };
          }),
        };
      });
    }
  };

  const removeLocationFieldByIndex = (index: number) => {
    setPrimarySectionFilters((current) => (current ? ({
      ...current,
      primarySection: {
        ...current.primarySection,
        selections: current.primarySection.selections.filter((_, itemIndex) => itemIndex !== index),
      },
    }) : current));
  };

  const removeSecondaryFieldByIndex = (index: number) => {
    setPrimarySectionFilters((current) => (current ? ({
      ...current,
      secondaries: current.secondaries.filter((_, itemIndex) => itemIndex !== index),
    }) : current));
  };

  const removeLocationSubFieldByIndex = (index: number, subIndex: number) => {
    setPrimarySectionFilters((current) => (current ? ({
      ...current,
      primarySection: {
        ...current.primarySection,
        selections: current.primarySection.selections.map((item, itemIndex) => (itemIndex === index ? ({
          ...item,
          filters: (item as IMarketplaceFilterLocation).filters.filter((_, filterIndex) => filterIndex !== subIndex),
        } as IMarketplaceFilterLocation) : item)),
      },
    }) : current));
  };

  const removeSecondaryLocationSubFieldByIndex = (index: number, subIndex: number) => {
    setPrimarySectionFilters((current) => (current ? ({
      ...current,
      secondaries: current.secondaries.map((item, itemIndex) => (itemIndex === index ? ({
        ...item,
        filters: (item as IMarketplaceFilterLocation).filters.filter((_, filterIndex) => filterIndex !== subIndex),
      } as IMarketplaceFilterLocation) : item)),
    }) : current));
  };

  const removePrimarySectionTagByIndex = (index: number) => {
    setPrimarySectionFilters((current) => (current ? ({
      ...current,
      primarySection: {
        ...current.primarySection,
        tag: {
          ...current.primarySection.tag,
          selectedProperties: current.primarySection.tag.selectedProperties.filter((_, itemIndex) => itemIndex !== index),
        },
      },
    }) : current));
  };

  const removePrimarySelectedTagByIndex = (index: number, subIndex: number) => {
    setPrimarySectionFilters((current) => (current ? ({
      ...current,
      secondaries: current.secondaries.map((item, itemIndex) => (itemIndex === index ? ({
        ...item,
        selectedProperties: (item as IMarketplaceFilterTagSelectedValues).selectedProperties
          .filter((_, propertyIndex) => propertyIndex !== subIndex),
      } as IMarketplaceFilterTagSelectedValues) : item)),
    }) : current));
  };

  const locationButtonOnClick = (type: LocationButtonsEnum, index: number) => {
    if (marketplace) {
      setPrimarySectionFilters((current) => {
        if (!current) return current;
        return {
          ...current,
          primarySection: {
            ...current.primarySection,
            selections: current.primarySection.selections.map((item, itemIndex) => {
              if (itemIndex !== index) return item;
              const location = item as IMarketplaceFilterLocation;
              return {
                ...location,
                filters: [...location.filters, {
                  id: RandomInteger().toString(),
                  type: IMarketplaceFilterType.NestedAllValuesTag,
                  subType: type,
                  sortOrder: location.filters.length + 1,
                  name: { [marketplace?.language]: '' },
                  callToActionText: null,
                  accordionState: 'none',
                  propertyValuesOrder: 'A-Z_0-9',
                  property: null,
                  subFilters: type === LocationButtonsEnum.elementsList ? [] : [{
                    id: RandomInteger().toString(),
                    type: IMarketplaceFilterType.NestedAllValuesTag,
                    subType: type,
                    sortOrder: 0,
                    name: { [marketplace?.language]: '' },
                    callToActionText: null,
                    accordionState: 'none',
                    propertyValuesOrder: 'A-Z_0-9',
                    property: null,
                    subFilters: [],
                  }],
                }],
              };
            }),
          },
        };
      });
    }
  };

  const secondaryLocationButtonOnClick = (type: LocationButtonsEnum, index: number) => {
    if (marketplace) {
      setPrimarySectionFilters((current) => {
        if (!current) return current;
        return {
          ...current,
          secondaries: current.secondaries.map((item, itemIndex) => {
            if (itemIndex !== index) return item;
            const location = item as IMarketplaceFilterLocation;
            return {
              ...location,
              filters: [...location.filters, {
                id: RandomInteger().toString(),
                type: IMarketplaceFilterType.NestedAllValuesTag,
                subType: type,
                sortOrder: location.filters.length + 1,
                name: { [marketplace?.language]: '' },
                callToActionText: null,
                accordionState: 'none',
                propertyValuesOrder: 'A-Z_0-9',
                property: null,
                subFilters: type === LocationButtonsEnum.elementsList ? [] : [{
                  id: RandomInteger().toString(),
                  type: IMarketplaceFilterType.NestedAllValuesTag,
                  subType: type,
                  sortOrder: 0,
                  name: { [marketplace?.language]: '' },
                  callToActionText: null,
                  accordionState: 'none',
                  propertyValuesOrder: 'A-Z_0-9',
                  property: null,
                  subFilters: [],
                }],
              }],
            };
          }),
        };
      });
    }
  };

  const onSave = async () => {
    setIsLoading(true);
    if (primarySectionFilters && marketplace) {
      const saveData: IMarketplace = {
        ...marketplace,
        frontConfiguration: {
          ...marketplace.frontConfiguration,
          module: {
            ...marketplace.frontConfiguration.module,
            filtersConfiguration: {
              ...primarySectionFilters,
            },
          },
        },
      };
      let res;

      if (marketplace) {
        res = await putMarketplace(jwt, marketplace.id, saveData);
      } else {
        res = await postMarketplace(jwt, primarySectionFilters);
      }

      if (res.ok) {
        toastrClear();
        toastrSuccess();
        fetchMarketplaces();
        setIsLoading(false);
      } else {
        setError(res.data['hydra:description']);
        setIsLoading(false);
      }
    }
  };

  const crumbs: ICrumbs[] = [
    {
      name: translations.marketplace,
      url: `/profile/${pathList.marketplace}`,
    },
    {
      name: `${marketplace?.name[marketplace?.language]} set-up`,
      url: `/profile/${pathList.marketplace}/${marketplace?.id}`,
    },
    {
      name: 'Filters',
    },
  ];
  return (
    <Box py={5} px={4}>
      <form onSubmit={handleSubmit(onSave)}>
        <Box mb={8} display="flex" flexWrap="nowrap">
          <Box fontSize="32px" mb={1} fontWeight={700} mr="auto">
            { translations.marketplaceComponents }
          </Box>
          <Box>
            <ButtonApp
              text={translations.saveChanges}
              color="primary"
              variant="contained"
              type="submit"
              disabled={isLoading}
            />
          </Box>
        </Box>
        <Box mb={5}>
          <Breadcrumbs crumbs={crumbs} />
        </Box>
        <Box className={styles.filtersWrapper}>
          <div className="primary-selection-silters">
            <Box fontSize="24px" mb={8} fontWeight={700}>
              { translations.primarySelectionFilters }
            </Box>
            <div className={styles.actions}>
              <ActionButtons
                buttons={primarySelectionFilters}
                onClick={primarySelectionFiltersOnClick}
              />
            </div>
            {primarySectionFilters && (
              <ReactSortable
                animation={150}
                handle=".drag-handle"
                list={primarySectionFilters.primarySection.selections.map((entity) => ({ ...entity, chosen: false }))}
                setList={(newList, sortable: Sortable | null, store: Store) => {
                  if (store && store.dragging && store.dragging.props && JSON.stringify(store.dragging.props.list) !== JSON.stringify(newList)) {
                    const orderedList = newList.map((item, index) => ({ ...item, sortOrder: index }));
                    setPrimarySectionFilters({
                      ...primarySectionFilters,
                      primarySection: {
                        ...primarySectionFilters.primarySection,
                        selections: orderedList,
                        tag: primarySectionFilters?.primarySection.tag,
                      },
                      secondaries: [...primarySectionFilters?.secondaries],
                    });
                  }
                }}
              >
                <>
                  { primarySectionFilters?.primarySection.selections.map((item, index) => {
                    if (item.type === IMarketplaceFilterType.GroupingFilter) {
                      return (
                        <div key={item.id} className={`${styles.locationSection} section`}>
                          <LocationGroup
                            item={item}
                            lang={marketplace?.language || ''}
                            onTrash={() => removeLocationFieldByIndex(index)}
                            onChange={updatePrimarySelection}
                          />
                          <div className={styles.locationActions}>
                            <LocationButtons onClick={(type) => locationButtonOnClick(type, index)} />
                          </div>

                          <Box display="flex" flexDirection="column" gap={8}>
                            <ReactSortable
                              animation={150}
                              handle=".drag-handle"
                              list={(item as IMarketplaceFilterLocation).filters.map((entity) => ({ ...entity, chosen: false }))}
                              setList={(newList, sortable: Sortable | null, store: Store) => {
                                if (store && store.dragging && store.dragging.props
                                  && JSON.stringify(store.dragging.props.list) !== JSON.stringify(newList)) {
                                  const temp = { ...item, filters: newList.map((listItem, listItemIndex) => ({ ...listItem, sortOrder: listItemIndex })) };
                                  updatePrimarySelection(temp as IMarketplaceFilterLocation);
                                }
                              }}
                            >
                              { (item as IMarketplaceFilterLocation).filters.map((filter, subIndex) => {
                                return (
                                  <div key={filter.id}>
                                    { !filter.subFilters.length && filter.type === IMarketplaceFilterType.NestedAllValuesTag && (
                                      <SubGroupWrapper
                                        name="Location: Elements list"
                                        onTrash={() => removeLocationSubFieldByIndex(index, subIndex)}
                                      >
                                        <LocationSubGroupElements
                                          item={filter}
                                          lang={marketplace?.language || ''}
                                          locationOptions={locationMarketplacePropertyOptions}
                                          onChange={(updatedFilter) => updatePrimaryLocationFilter(item.id, updatedFilter)}
                                        />
                                      </SubGroupWrapper>
                                    ) }
                                    { !!filter.subFilters.length && (filter?.subType === LocationButtonsEnum.locationGroup || filter.type === IMarketplaceFilterType.NestedAllValuesTag) && (
                                      <SubGroupWrapper
                                        name="Location: Location group"
                                        onTrash={() => removeLocationSubFieldByIndex(index, subIndex)}
                                      >
                                        <LocationSubGroupLocationGroup
                                          item={filter}
                                          lang={marketplace?.language || ''}
                                          locationOptions={locationMarketplacePropertyOptions}
                                          onChange={(updatedFilter) => updatePrimaryLocationFilter(item.id, updatedFilter)}
                                        />
                                      </SubGroupWrapper>
                                    ) }
                                  </div>
                                );
                              }) }
                            </ReactSortable>
                          </Box>
                        </div>
                      );
                    }
                    if (item.type === IMarketplaceFilterType.AllValuesTag) {
                      return (
                        <div key={item.id} className={`${styles.tagSection} section`}>
                          <TagAllValues
                            item={(item as IMarketplaceFilterTagAllValues)}
                            lang={marketplace?.language || ''}
                            onTrash={() => removeLocationFieldByIndex(index)}
                            propertyOptions={marketplacePropertyOptions}
                            onChange={updatePrimarySelection}
                          />
                        </div>
                      );
                    }
                    if (item.type === IMarketplaceFilterType.AllValuesCheckbox) {
                      return (
                        <div key={item.id} className={`${styles.checkboxSection} section`}>
                          <Checkbox
                            item={(item as IMarketplaceFilterTagAllValues)}
                            lang={marketplace?.language || ''}
                            onTrash={() => removeLocationFieldByIndex(index)}
                            propertyOptions={marketplacePropertyOptions}
                            onChange={updatePrimarySelection}
                          />
                        </div>
                      );
                    }
                    if (item.type === IMarketplaceFilterType.AllValuesRange) {
                      return (
                        <div key={item.id} className={`${styles.rangeSection} section`}>
                          <Range
                            item={(item as IMarketplaceFilterRange)}
                            lang={marketplace?.language || ''}
                            onTrash={() => removeLocationFieldByIndex(index)}
                            propertyOptions={marketplacePropertyOptions}
                            onChange={updatePrimarySelection}
                          />
                        </div>
                      );
                    }
                    return null;
                  }) }
                </>
              </ReactSortable>
            )}
          </div>
          <div className={styles.primarySelectionTags}>
            <Box fontSize="24px" mb={8} fontWeight={700}>
              { translations.primarySelectionTags }
            </Box>
            {primarySectionFilters?.primarySection.tag && (
              <>
                <div className="section">
                  <TagFilter
                    item={primarySectionFilters?.primarySection.tag}
                    lang={marketplace?.language || ''}
                    onChange={updatePrimaryTag}
                  />
                </div>
                <div className={styles.actions}>
                  <ActionButtons
                    buttons={primarySelectionTags}
                    onClick={primarySelectionTagsOnClick}
                  />
                </div>
                <Box display="flex" flexDirection="column" gap={8}>
                  {primarySectionFilters?.primarySection?.tag?.selectedProperties.length && (
                    <ReactSortable
                      animation={150}
                      handle=".drag-handle"
                      list={primarySectionFilters.primarySection.tag.selectedProperties}
                      setList={(newList, sortable: Sortable | null, store: Store) => {
                        if (store && store.dragging && store.dragging.props
                          && JSON.stringify(store.dragging.props.list) !== JSON.stringify(newList)) {
                          const orderedList = newList.map((item, index) => ({ ...item, sortOrder: index }));
                          setPrimarySectionFilters({
                            ...primarySectionFilters,
                            primarySection: {
                              ...primarySectionFilters.primarySection,
                              selections: [...primarySectionFilters?.primarySection.selections],
                              tag: {
                                ...primarySectionFilters?.primarySection.tag,
                                selectedProperties: orderedList,
                              },
                            },
                            secondaries: [...primarySectionFilters?.secondaries],
                          });
                        }
                      }}
                    >
                      { marketplace && allSelectedProperties && primarySectionFilters?.primarySection.tag.selectedProperties.map((property, index) => (
                        <div key={property.id}>
                          <SubGroupWrapper
                            name="Primary tag filter (selected values)"
                            onTrash={() => removePrimarySectionTagByIndex(index)}
                          >
                            <PrimaryTagFilter
                              marketplace={marketplace}
                              item={property}
                              lang={marketplace?.language || ''}
                              isNeedToChange={isNeedToBeUpdated(property.property?.systemName, property.value, allSelectedProperties)}
                              propertyOptions={marketplacePropertyOptions}
                              onChange={updatePrimaryTagProperty}
                            />
                          </SubGroupWrapper>
                        </div>
                      )) }
                    </ReactSortable>
                  ) }
                </Box>
              </>
            ) }
          </div>
          <div className="secondary-filters">
            <Box fontSize="24px" mb={8} fontWeight={700}>
              { translations.secondaryFilters }
            </Box>
            <div className={styles.actions}>
              <ActionButtons
                buttons={secondaryFilters}
                onClick={secondarySelectionFiltersOnClick}
              />
            </div>
            {primarySectionFilters?.secondaries && (
              <ReactSortable
                animation={150}
                handle=".drag-handle"
                list={primarySectionFilters.secondaries.map((entity) => ({ ...entity, chosen: false }))}
                setList={(newList, sortable: Sortable | null, store: Store) => {
                  if (store && store.dragging && store.dragging.props
                    && JSON.stringify(store.dragging.props.list) !== JSON.stringify(newList)) {
                    const orderedList = newList.map((item, index) => ({ ...item, sortOrder: index }));
                    setPrimarySectionFilters({
                      ...primarySectionFilters,
                      secondaries: [...orderedList],
                    });
                  }
                }}
              >
                <>
                  { primarySectionFilters.secondaries.map((item, index) => {
                    if (item.type === IMarketplaceFilterType.GroupingFilter) {
                      return (
                        <div key={item.id} className={`${styles.locationSection} section`}>
                          <LocationGroup
                            item={item}
                            lang={marketplace?.language || ''}
                            onTrash={() => removeSecondaryFieldByIndex(index)}
                            onChange={updateSecondarySelection}
                          />
                          <div className={styles.locationActions}>
                            <LocationButtons onClick={(type) => secondaryLocationButtonOnClick(type, index)} />
                          </div>
                          <Box display="flex" flexDirection="column" gap={8}>
                            <ReactSortable
                              animation={150}
                              handle=".drag-handle"
                              list={(item as IMarketplaceFilterLocation).filters.map((entity) => ({
                                ...entity,
                                chosen: false,
                              }))}
                              setList={(newList, sortable: Sortable | null, store: Store) => {
                                if (store && store.dragging && store.dragging.props && JSON.stringify(store.dragging.props.list) !== JSON.stringify(newList)) {
                                  const temp = { ...item, filters: newList.map((listItem, listItemIndex) => ({ ...listItem, sortOrder: listItemIndex })) };
                                  updateSecondarySelection(temp as IMarketplaceFilterLocation);
                                }
                              }}
                            >
                              { (item as IMarketplaceFilterLocation).filters.map((filter, subIndex) => {
                                return (
                                  <div key={filter.id}>
                                    { !filter.subFilters.length && filter.type === IMarketplaceFilterType.NestedAllValuesTag && (
                                      <SubGroupWrapper
                                        name="Location: Elements list"
                                        onTrash={() => removeSecondaryLocationSubFieldByIndex(index, subIndex)}
                                      >
                                        <LocationSubGroupElements
                                          item={filter}
                                          lang={marketplace?.language || ''}
                                          locationOptions={locationMarketplacePropertyOptions}
                                          onChange={(updatedFilter) => updateSecondaryLocationFilter(item.id, updatedFilter)}
                                        />
                                      </SubGroupWrapper>
                                    ) }
                                    { !!filter.subFilters.length && (filter?.subType === LocationButtonsEnum.locationGroup || filter.type === IMarketplaceFilterType.NestedAllValuesTag) && (
                                      <SubGroupWrapper
                                        name="Location: Location group"
                                        onTrash={() => removeSecondaryLocationSubFieldByIndex(index, subIndex)}
                                      >
                                        <LocationSubGroupLocationGroup
                                          item={filter}
                                          lang={marketplace?.language || ''}
                                          locationOptions={locationMarketplacePropertyOptions}
                                          onChange={(updatedFilter) => updateSecondaryLocationFilter(item.id, updatedFilter)}
                                        />
                                      </SubGroupWrapper>
                                    ) }
                                  </div>
                                );
                              }) }
                            </ReactSortable>
                          </Box>
                        </div>
                      );
                    }
                    if (item.type === IMarketplaceFilterType.AllValuesTag) {
                      return (
                        <div key={item.id} className={`${styles.tagSection} section`}>
                          <TagAllValues
                            item={(item as IMarketplaceFilterTagAllValues)}
                            lang={marketplace?.language || ''}
                            onTrash={() => removeSecondaryFieldByIndex(index)}
                            propertyOptions={marketplacePropertyOptions}
                            onChange={updateSecondarySelection}
                          />
                        </div>
                      );
                    }
                    if (item.type === IMarketplaceFilterType.AllValuesRange) {
                      return (
                        <div key={item.id} className={`${styles.rangeSection} section`}>
                          <Range
                            item={(item as IMarketplaceFilterRange)}
                            lang={marketplace?.language || ''}
                            onTrash={() => removeSecondaryFieldByIndex(index)}
                            propertyOptions={marketplacePropertyOptions}
                            onChange={updateSecondarySelection}
                          />
                        </div>
                      );
                    }
                    if (item.type === IMarketplaceFilterType.AllValuesCheckbox) {
                      return (
                        <div key={item.id} className={`${styles.checkboxSection} section`}>
                          <Checkbox
                            item={(item as IMarketplaceFilterTagAllValues)}
                            lang={marketplace?.language || ''}
                            onTrash={() => removeSecondaryFieldByIndex(index)}
                            propertyOptions={marketplacePropertyOptions}
                            onChange={updateSecondarySelection}
                          />
                        </div>
                      );
                    }
                    if (item.type === IMarketplaceFilterType.SelectedValuesTag) {
                      return (
                        <div key={item.id} className={`${styles.checkboxSection} section`}>
                          <div>
                            <TagFilter
                              item={(item as IMarketplaceFilterTagSelectedValues)}
                              lang={marketplace?.language || ''}
                              onTrash={() => removeSecondaryFieldByIndex(index)}
                              onDrag={() => {}}
                              onChange={updateSecondarySelection}
                            />
                          </div>
                          <div className={styles.actions}>
                            <ActionButtons
                              buttons={primarySelectionTags}
                              onClick={() => primarySelectedTagOnClick(index)}
                            />
                          </div>
                          <Box display="flex" flexDirection="column" gap={8}>
                            <ReactSortable
                              animation={150}
                              handle=".drag-handle"
                              list={(item as IMarketplaceFilterTagSelectedValues).selectedProperties.map((entity) => ({
                                ...entity,
                                chosen: false,
                              }))}
                              setList={(newList, sortable: Sortable | null, store: Store) => {
                                if (store && store.dragging && store.dragging.props
                                  && JSON.stringify(store.dragging.props.list) !== JSON.stringify(newList)) {
                                  const orderedList = newList.map((listItem, listItemIndex) => ({
                                    ...listItem,
                                    sortOrder: listItemIndex,
                                  }));
                                  const tmp = { ...(item as IMarketplaceFilterTagSelectedValues), selectedProperties: orderedList };
                                  updateSecondarySelection(tmp);
                                }
                              }}
                            >
                              { (item as IMarketplaceFilterTagSelectedValues).selectedProperties.map((property, propertyIndex) => (
                                <div key={property.id}>
                                  <SubGroupWrapper
                                    name="Primary tag filter (selected values)"
                                    onTrash={() => removePrimarySelectedTagByIndex(index, propertyIndex)}
                                  >
                                    <PrimaryTagFilter
                                      marketplace={marketplace}
                                      item={property}
                                      lang={marketplace?.language || ''}
                                      isNeedToChange={false}
                                      propertyOptions={marketplacePropertyOptions}
                                      onChange={(updatedProperty) => updateSecondaryTagProperty(item.id, updatedProperty)}
                                    />
                                  </SubGroupWrapper>
                                </div>
                              )) }
                            </ReactSortable>
                          </Box>
                        </div>
                      );
                    }
                    return null;
                  }) }
                </>
              </ReactSortable>
            ) }
          </div>
          <div className="additional-settings">
            <Box fontSize="24px" mb={8} fontWeight={700}>
              { translations.additionalSettings }
            </Box>
            {primarySectionFilters && (
              <AdditionalSettings
                item={primarySectionFilters}
                onChange={setPrimarySectionFilters}
              />
            ) }
          </div>
        </Box>
      </form>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} elevation={6} variant="filled" severity="error">
          { error }
        </Alert>
      </Snackbar>
    </Box>
  );
};
